import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { match } from 'ts-pattern';
import { useSnackbar } from 'ui-components';
import { ContentDetailModal } from '~/components/ContentDetailModal';
import { PostedAtDateTimePickerModal } from '~/components/ContentSearchPostedAtTimeframe/PostedAtDateTimePickerModal';
import DocumentTitle from '~/components/DocumentTitle';
import { Feature } from '~/components/Feature';
import { FEATURE_FLAGS } from '~/constants';
import { logger } from '~/core/logger/logger';
import { useGetAgency } from '~/hooks/api/agencies/get';
import { useGetMediaKit } from '~/hooks/api/media-kits/get';
import { BrandExperienceModal } from '~/screens/PageBuilder/modals/BrandExperienceModal';

import {
  AVAILABLE_FONTS,
  getDefaultMKColors,
} from '../AgencySettingsScreen/components/GeneralTab/components/AgencyBranding/constants';
import {
  applyMediaKitFont,
  getMediaKitColors,
} from '../AgencySettingsScreen/components/GeneralTab/components/AgencyBranding/utils';
import { BuilderCanvas } from './canvas';
import { usePostedBy } from './hooks/usePostedBy';
import { ModalPlatformAnalytics } from './modals/PlatformAnalytics';
import { VideoUrlModal } from './modals/VideoUrlModal/VideoUrlModal';
import { ModalAddContent } from './sidebar/ModalAddContent';
import { ModalSelectCreator } from './sidebar/ModalSelectCreator';
import { useAddContentStore } from './sidebar/useAddContentStore';

export const MEDIA_KITS_DD_SECTION = 'media-kits';

export const PageBuilder = () => {
  const router = useRouter();
  const snackbar = useSnackbar();
  const id = router.query.id as string;

  const mediaKit = useGetMediaKit({ id });
  const {
    isCustomModalOpen,
    isContentDetailModalOpen,
    contentId,
    setIsCustomModalOpen,
    setIsContentDetailModalOpen,
    isModalOpen,
  } = useAddContentStore();
  const { handleSelectCustomDates } = usePostedBy();

  const { data: agency } = useGetAgency(mediaKit.data?.agency_id as string);

  useEffect(() => {
    const brandPrimaryColor = agency?.theming?.primaryColor || '';
    const brandSecondaryColor = agency?.theming?.secondaryColor || '';
    const brandBackgroundColor = agency?.theming?.backgroundColor || '#fff';
    const brandFont = agency?.theming?.font || null;

    const mkColors = match(isModalOpen)
      .with(false, () =>
        getMediaKitColors({
          primaryColor: brandPrimaryColor,
          secondaryColor: brandSecondaryColor,
          backgroundColor: brandBackgroundColor,
        }),
      )
      .otherwise(() => getMediaKitColors({}));

    Object.entries(mkColors).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value as string);
    });

    const font = AVAILABLE_FONTS.find((f) => f.name === brandFont);
    applyMediaKitFont(font);

    // Cleanup: reset CSS variables to defaults when component unmounts
    return () => {
      const defaultColors = getDefaultMKColors();
      Object.entries(defaultColors).forEach(([key, value]) => {
        document.documentElement.style.setProperty(key, value);
      });
    };
  }, [agency, isModalOpen]);

  useEffect(() => {
    if (!mediaKit.error) return;

    if ((mediaKit.error as any)?.response?.status === 404) {
      logger
        .withMetadata({
          mediaKitId: id,
          section: MEDIA_KITS_DD_SECTION,
        })
        .error('Media Kit not found');

      snackbar.error({
        description: 'This Media Kit is not available.',
        duration: 9000,
        isCloseable: true,
      });
      router.push('/media-kits');
      return;
    }

    logger
      .withMetadata({
        mediaKitId: id,
        section: MEDIA_KITS_DD_SECTION,
      })
      .error('Media Kit loading error');
  }, [mediaKit.error, router, snackbar]);

  return (
    <>
      <DocumentTitle>{mediaKit.data?.name as string}</DocumentTitle>

      <main
        id="builder-area"
        className="h-full"
        style={{ fontFamily: 'var(--media-kit-font-family, inherit)' }}
      >
        <BuilderCanvas />
      </main>

      <ModalSelectCreator />
      <ModalPlatformAnalytics />
      <Feature name={FEATURE_FLAGS.foamMediaKitsContent}>
        <ModalAddContent />
      </Feature>
      <PostedAtDateTimePickerModal
        isOpen={isCustomModalOpen}
        onClose={() => setIsCustomModalOpen(false)}
        onFilter={(postedAfter, postedBefore) =>
          handleSelectCustomDates({ postedAfter, postedBefore })
        }
        postedBefore=""
        postedAfter=""
      />

      <ContentDetailModal
        isOpen={isContentDetailModalOpen}
        onClose={() => setIsContentDetailModalOpen(false, undefined)}
        id={contentId}
      />
      <VideoUrlModal />
      <BrandExperienceModal />
    </>
  );
};
